import { useQuery } from "@tanstack/react-query";
import { formatDuration, intervalToDuration } from "date-fns";
import { fr } from "date-fns/locale";
import { Plus } from "lucide-react";

import type { ReservationItinerary } from "@upclaim/sdk/src/data-contracts";

import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import adminAPI from "@/services/api";

import type { ClaimTypeName } from "../../lib/claim-types";
import type { EffectiveAirline, OverviewFlight } from "../flight-overview";
import { FlightOverview } from "../flight-overview";
import { FullReservationItinerarySkeleton } from "./full-reservation-itinerary-skeleton";

type ItinerarySegment = ReservationItinerary["segments"][number];

interface FullReservationItineraryProps {
  claimId: string;
  reservationId: string;
  impactedSegmentId: number | null;
  claimType: ClaimTypeName;
}

function formatConnectionTime(
  flightA: ItinerarySegment["flight"],
  flightB: ItinerarySegment["flight"] | undefined,
): string | undefined {
  if (!flightB) {
    return undefined;
  }
  const duration = intervalToDuration({
    start: new Date(flightA.scheduledArrivalUTC),
    end: new Date(flightB.scheduledDepartureUTC),
  });
  return formatDuration(duration, { locale: fr });
}

function SectionSeparator({ label, className }: { label: string; className?: string }) {
  return (
    <div className={cn("relative px-4", className)}>
      <div className="absolute inset-0 flex items-center" aria-hidden="true">
        <div className="w-full border-t border-gray-300" />
      </div>
      <div className="relative flex justify-start">
        <span className="bg-white px-3 text-base font-semibold leading-6 text-gray-900">{label}</span>
      </div>
    </div>
  );
}

/** Port of FullReservationItinerary.vue — full itinerary of the reservation, segment by segment. */
export function FullReservationItinerary({
  claimId,
  reservationId,
  impactedSegmentId,
  claimType,
}: FullReservationItineraryProps) {
  const { data: segments } = useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "reservation-itinerary", reservationId],
    queryFn: async () => {
      const reservation = await adminAPI.retrieveReservationItinerary(reservationId);
      return reservation.segments?.filter((s) => s.cancelledBy !== "CUSTOMER");
    },
  });

  return (
    <div className="py-2">
      <SectionSeparator label="Début de la réservation" />

      {!segments?.length ? (
        <FullReservationItinerarySkeleton />
      ) : (
        <div>
          {segments.map((segment, index) => (
            <div key={segment.id} className="text-gray-800">
              <div
                className={cn(
                  impactedSegmentId === segment.id &&
                    "relative mx-1 my-5 rounded-lg border-2 border-indigo-500 bg-indigo-50",
                  segment.reroutingProposal &&
                    segment.confirmationDate &&
                    !segment.cancelledBy &&
                    "rounded-lg border-2 border-dotted border-green-500 bg-green-50",
                  segment.reroutingProposal &&
                    !segment.confirmationDate &&
                    "rounded-lg border-2 border-dashed border-gray-500 bg-gray-100",
                )}
              >
                {impactedSegmentId === segment.id ? (
                  <div className="ml-2 mt-4 flex">
                    <span className="px-3 italic text-indigo-700">Segment detecté</span>
                  </div>
                ) : null}
                {segment.reroutingProposal ? (
                  <div className="ml-2 mt-4">
                    {segment.confirmationDate && !segment.cancelledBy ? (
                      <span className="px-3 font-semibold italic leading-8 text-green-800">
                        Proposition de réacheminement confirmée
                      </span>
                    ) : (
                      <span className="px-3 italic leading-8 text-gray-800">
                        Proposition de réacheminement non confirmée
                      </span>
                    )}
                  </div>
                ) : null}
                {segment.cancelledBy !== "CUSTOMER" ? (
                  <FlightOverview
                    claimId={claimId}
                    airline={segment.flight.airline as unknown as EffectiveAirline}
                    claimTypeName={impactedSegmentId === segment.id ? claimType : null}
                    flight={segment.flight as unknown as OverviewFlight}
                    reroutingProposals={segment.reroutingProposals}
                    actualRerouting={segment.actualRerouting}
                    cancelledBy={segment.cancelledBy}
                    cancelledIndicator={segment.cancellationType}
                    cancelledSignature={segment.cancellationSignature}
                    cancelledDate={segment.cancellationDate}
                  />
                ) : null}
              </div>

              {formatConnectionTime(segment.flight, segments[index + 1]?.flight) ? (
                <div className="relative py-2">
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className="w-full border-t border-dashed border-gray-300" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="inline-flex items-center gap-x-1.5 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300">
                      <Plus className="-ml-1 -mr-0.5 h-5 w-5 text-gray-400" aria-hidden="true" />
                      {formatConnectionTime(segment.flight, segments[index + 1]?.flight)}
                    </span>
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      <SectionSeparator label="Fin de la réservation" className="mt-8" />
    </div>
  );
}
